import { DetailedVehicle, useCars } from "Api/registration";
import { fetchCartStart } from "Store/cart/cartSlice";
import { INSURANCE_NAME_VALUE } from "Store/interfaces/products";
import { CarProduct } from "Store/interfaces/products/checkout/riskProducts/vehicleProducts/CarProduct";
import { ReduxState } from "Store/rootReducer";
import { isAuthenticated } from "Utils/KeycloakWrapper";
import { getCartIdFromCookie } from "Utils/cookies/cookiesUtil";
import { isDefined } from "Utils/functions";
import { FormikProps } from "formik";
import { useCallback, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { VehicleInputType } from "../../FormElements/interfaces";
import { CarProductIntroFormValues } from "../interfaces";
import { resetVehicle } from "../utils/resetVehicle";
import { setVehicle } from "../utils/setVehicle";

export const STOREBRAND_COMPANY_CODE = 6500;

export const useViewModel = (
  formProps: FormikProps<CarProductIntroFormValues>,
): {
  data: {
    vehicles: Array<DetailedVehicle & { isAddedToCart: boolean }>;
    isLoading: boolean;
    error: unknown;
    isAlreadyInsured: boolean;
    renderView: boolean;
  };
  handlers: {
    selectVehicle: (vehicle: DetailedVehicle) => void;
  };
} => {
  const { values } = formProps;

  const { data: vehicles, error, isLoading } = useCars();

  useLicensePlateSearchOnEmptyRegisteredCars(vehicles, error, formProps);

  const selectVehicle = useCallback(
    (vehicle: DetailedVehicle): void => {
      resetVehicle(formProps);
      setVehicle(formProps, vehicle);
      formProps.setFieldValue("modelType", vehicle.displayName);
      formProps.setFieldValue("vehicleInputType", VehicleInputType.OWNED);
    },
    [vehicles],
  );

  const carsInCart = useVehiclesFromCart();
  const aggregatedCars = vehicles?.map((vehicle) => {
    const fromCart = carsInCart?.find(
      (vehicleInCart) => vehicleInCart.licensePlateNumber === vehicle.regNumber,
    );
    return { ...vehicle, isAddedToCart: isDefined(fromCart) };
  });

  return {
    data: {
      vehicles: aggregatedCars ?? [],
      isLoading,
      error,
      isAlreadyInsured: Boolean(values.isAlreadyInsured),
      renderView: isAuthenticated(),
    },
    handlers: {
      selectVehicle,
    },
  };
};

const useLicensePlateSearchOnEmptyRegisteredCars = (
  vehicles: Array<DetailedVehicle> | null,
  error: unknown,
  formProps: FormikProps<CarProductIntroFormValues>,
): void => {
  useEffect(() => {
    if (
      ((isDefined(vehicles) && vehicles.length === 0) || isDefined(error)) &&
      formProps.values.vehicleInputType === VehicleInputType.OWNED
    ) {
      formProps.setFieldValue(
        "vehicleInputType",
        VehicleInputType.LICENSE_PLATE,
      );
    }
  }, [vehicles, error, formProps]);
};

const useVehiclesFromCart = (): Array<CarProduct> | undefined => {
  const dispatch = useDispatch();
  useEffect(() => {
    const cartId = getCartIdFromCookie();
    if (isDefined(cartId)) {
      dispatch(fetchCartStart({ cartId, forceRecalculate: false }));
    }
  }, []);

  const cart = useSelector((state: ReduxState) => state.cart.current);

  if (!isDefined(cart)) {
    return;
  }

  return cart.products.filter(
    (product): product is CarProduct =>
      product.name === INSURANCE_NAME_VALUE.CAR,
  );
};
